/**
 * ShoppingCartModalContext
 *
 * Global context for managing the shopping cart modal state.
 * This allows the cart modal to be opened from anywhere in the app
 * (menu bar, store page, etc.) while maintaining a single modal instance.
 */

import React, { createContext, useContext, useState, ReactNode } from "react";

interface ShoppingCartModalContextType {
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  toggleCart: () => void;
}

const ShoppingCartModalContext = createContext<
  ShoppingCartModalContextType | undefined
>(undefined);

interface ShoppingCartModalProviderProps {
  children: ReactNode;
}

export const ShoppingCartModalProvider: React.FC<
  ShoppingCartModalProviderProps
> = ({ children }) => {
  const [isCartOpen, setIsCartOpen] = useState(false);

  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const toggleCart = () => setIsCartOpen((prev) => !prev);

  const value = {
    isCartOpen,
    openCart,
    closeCart,
    toggleCart,
  };

  return (
    <ShoppingCartModalContext.Provider value={value}>
      {children}
    </ShoppingCartModalContext.Provider>
  );
};

export const useShoppingCartModal = (): ShoppingCartModalContextType => {
  const context = useContext(ShoppingCartModalContext);
  if (context === undefined) {
    throw new Error(
      "useShoppingCartModal must be used within a ShoppingCartModalProvider"
    );
  }
  return context;
};
